import {
  Box,
  Button,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Spinner,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AppIcons } from "constants/icons";
import { ArtifactProto } from "build-ts/chat_session";
import { DarwinHeader } from "build-ts/common";
import { Header } from "build-ts/metadata";
import { ScheduledJobProto } from "build-ts/schedule";
import MessageInputAddMenu from "../containers/MessageInputAddMenu";
import MessageInput, { type MessageInputHandle } from "./MessageInput";
import ArtifactMentionMenu, { type ArtifactMentionMenuHandle } from "./ArtifactMentionMenu";
import { ArtifactKind, ArtifactMap, buildArtifactSections } from "modules/artifacts/utils/artifactUtils";
import { labelMatchesQuery } from "modules/chat/utils/chatUtils";

export interface MessageInputBoxProps {
  chatId?: string;
  sessionId?: string;
  isLoading: boolean;
  isLoadingChat: boolean;
  isInputDisabledDueToEva: boolean;
  isBlockedByAnalystLoading: boolean;
  selectedAnalyst?: Header | null;
  analystsList?: Header[];
  isLoadingAnalysts?: boolean;
  isFromEva?: boolean;
  onAnalystChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  currentSessionHeader?: DarwinHeader;
  scenarioJobs?: ScheduledJobProto[];
  artifactMap?: ArtifactMap;
  lookupByName?: (name: string, kind?: ArtifactKind) => ArtifactProto | undefined;
  onSendMessage?: (
    msg: string,
    isNew: boolean,
    analyst: Header | null | undefined,
    csvFiles?: { file: File; fileName: string }[]
  ) => void | Promise<void>; /** Prompt preview text from hovered prompt buttons in the parent. */
  previewText?: string;
  /** Pre-fills the input on mount (e.g. workflow initialPrompt auto-send). */
  defaultMessage?: string;
  /** True when the chat already has messages — drives placeholder and min-height. */
  hasMessages: boolean;
  width: string;
}

const MessageInputBox: React.FC<MessageInputBoxProps> = ({
  chatId,
  sessionId,
  isLoading,
  isLoadingChat,
  isInputDisabledDueToEva,
  isBlockedByAnalystLoading,
  selectedAnalyst,
  analystsList = [],
  isLoadingAnalysts = false,
  isFromEva = false,
  onAnalystChange,
  currentSessionHeader,
  scenarioJobs,
  artifactMap,
  lookupByName,
  onSendMessage,
  previewText,
  defaultMessage,
  hasMessages,
  width,
}) => {
  const [inputMessage, setInputMessage] = useState<string>(defaultMessage ?? "");
  /** null = mention menu closed; string = active query text after '@' */
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  /** Files staged for upload — shown as attachment chips until the message is sent. */
  const [pendingFiles, setPendingFiles] = useState<{ file: File; fileName: string }[]>([]);
  /** True while the user is dragging files over the input wrapper. */
  const [isDragOver, setIsDragOver] = useState(false);

  const highlightedTextareaRef = useRef<MessageInputHandle>(null);
  const mentionMenuRef = useRef<ArtifactMentionMenuHandle>(null);
  /** Ref attached to the outer input wrapper Box — used to anchor the mention menu. */
  const inputWrapperRef = useRef<HTMLDivElement>(null);

  // Clear draft and mention state when the user switches to a different session.
  // Skip when sessionId is undefined (NewChat) so that defaultMessage is preserved.
  useEffect(() => {
    if (!sessionId) return;
    setInputMessage("");
    setMentionQuery(null);
    setPendingFiles([]);
  }, [sessionId]);

  // Input handlers
  const handleInputChange = useCallback((value: string) => {
    setInputMessage(value);
    // Show the @ mention menu when the user types @ followed by non-whitespace
    // (or just @ alone). Detect the last unfinished @query before the cursor.
    const match = value.match(/@([^\s@]*)$/);
    if (match) {
      setMentionQuery(match[1]);
    } else {
      setMentionQuery(null);
    }
  }, []);

  const handleSend = useCallback(() => {
    const msg = inputMessage;
    if (!msg.trim() && pendingFiles.length === 0) return;
    if (isBlockedByAnalystLoading) return;
    const isNew = !chatId && !sessionId;
    const csvFiles = pendingFiles.length > 0 ? [...pendingFiles] : undefined;
    // In continue mode, clear immediately so the message appears as a chat bubble.
    // In new-chat mode, keep the text visible during the session-init round-trip;
    // the component unmounts on navigate so no manual cleanup is needed.
    if (!isNew) {
      setInputMessage("");
      setPendingFiles([]);
    }
    onSendMessage?.(msg, isNew, isNew ? selectedAnalyst : undefined, csvFiles);
  }, [inputMessage, pendingFiles, chatId, sessionId, selectedAnalyst, isBlockedByAnalystLoading, onSendMessage]);

  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      // While the mention menu is open, arrow keys / Enter / Escape go to the menu.
      if (mentionQuery !== null && mentionMenuRef.current) {
        if (["ArrowUp", "ArrowDown", "Enter", "Escape"].includes(e.key)) {
          mentionMenuRef.current.handleKeyDown(e);
          return;
        }
      }
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (!isLoading && !isBlockedByAnalystLoading) handleSend();
      }
    },
    [isLoading, isBlockedByAnalystLoading, handleSend, mentionQuery]
  );

  const handleMentionSelect = useCallback((tag: string) => {
    highlightedTextareaRef.current?.replaceAtMention(tag);
    setMentionQuery(null);
  }, []);

  const handleAnalysisSelect = useCallback((tag: string) => {
    highlightedTextareaRef.current?.insertTag(tag);
  }, []);

  const handleCsvFileSelected = useCallback((files: File[]) => {
    const entries = files.map(file => ({ file, fileName: file.name }));
    setPendingFiles(prev => [...prev, ...entries]);
  }, []);

  const handleNonCsvFileSelected = useCallback((files: File[]) => {
    const entries = files.map(file => ({ file, fileName: file.name }));
    setPendingFiles(prev => [...prev, ...entries]);
  }, []);

  // Drag-and-drop handlers
  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    // Only clear when the pointer leaves the wrapper entirely, not when it moves to a child.
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragOver(false);
      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) handleNonCsvFileSelected(files);
    },
    [handleNonCsvFileSelected]
  );

  // Filtered mention sections
  const mentionSections = useMemo(() => {
    if (mentionQuery === null) return [];
    // Fall back to an empty map when there is no session yet (e.g. NewChatPage) so
    // buildArtifactSections still runs and returns workflow entries from scenarioJobs.
    const raw = buildArtifactSections(artifactMap ?? new Map(), scenarioJobs).filter(
      s => s.id !== "workflow-run-details" && s.id !== "workflow-run-searches"
    );
    const q = mentionQuery.toLowerCase();
    if (!q) return raw;
    const matches = (label: string) => labelMatchesQuery(label, q).matched;
    return raw.map(s => ({ ...s, items: s.items.filter(i => matches(i.label)) })).filter(s => s.items.length > 0);
  }, [artifactMap, mentionQuery, scenarioJobs]);

  // Analyst selector
  const renderAnalystSelector = useCallback(
    (iconSize: number, fontSize: "xs" | "sm") => {
      if (isFromEva) return null;
      if (isLoadingAnalysts) return <Spinner size="xs" color="purple.500" flexShrink={0} />;
      return (
        <Menu>
          <MenuButton
            as={Button}
            size="sm"
            maxW="330px"
            bg="purple.50"
            fontSize={fontSize}
            fontWeight="600"
            borderRadius="lg"
            color="gray.700"
            _hover={{ bg: "purple.100" }}
            _active={{ bg: "purple.100" }}
            rightIcon={<Icon as={AppIcons.downIcon} />}
            px={2}
            flexShrink={0}
            alignSelf="center"
          >
            <HStack spacing={1}>
              <Icon as={AppIcons.analyst} boxSize={iconSize} color="purple.600" />
              <Text isTruncated fontSize={fontSize} mb={0} fontWeight="600">
                {selectedAnalyst?.name || "Select analyst"}
              </Text>
            </HStack>
          </MenuButton>
          <Portal>
            <MenuList maxH="300px" overflowY="auto" zIndex={1500}>
              {analystsList.length === 0 ? (
                <MenuItem isDisabled fontSize="sm" color="gray.500">
                  No analysts available
                </MenuItem>
              ) : (
                analystsList?.map(analyst => {
                  const isSelected = analyst.id === selectedAnalyst?.id;
                  return (
                    <MenuItem
                      key={analyst.id}
                      onClick={() => onAnalystChange?.({ target: { value: analyst.id } } as any)}
                      fontSize="sm"
                      bg={isSelected ? "purple.50" : undefined}
                      _hover={{ bg: "purple.100" }}
                      _focus={{ bg: "purple.100" }}
                      fontWeight={isSelected ? "700" : "500"}
                      color={isSelected ? "purple.700" : undefined}
                    >
                      <HStack spacing={2} w="100%" justify="space-between">
                        <Text mb={0}>{analyst.name}</Text>
                        {isSelected && <Icon as={AppIcons.analyst} boxSize={3.5} color="purple.600" flexShrink={0} />}
                      </HStack>
                    </MenuItem>
                  );
                })
              )}
            </MenuList>
          </Portal>
        </Menu>
      );
    },
    [isFromEva, isLoadingAnalysts, selectedAnalyst, analystsList, onAnalystChange]
  );

  // Send button
  const renderSendButton = useCallback(
    (size: "sm" | "md", iconSize: number, flexShrink?: number) => (
      <Tooltip label="Analyst is loading, please wait" isDisabled={!isBlockedByAnalystLoading} hasArrow placement="top">
        <Box display="inline-flex" flexShrink={flexShrink}>
          <IconButton
            icon={
              isLoading || isBlockedByAnalystLoading ? (
                <Spinner size="xs" color="white" />
              ) : (
                <Icon as={AppIcons.sendArrow} boxSize={iconSize} color="white" />
              )
            }
            onClick={handleSend}
            bg="purple.600"
            isDisabled={isLoadingChat || isLoading || isInputDisabledDueToEva || isBlockedByAnalystLoading}
            aria-label="Send Chat Message"
            borderRadius="full"
            size={size}
            _hover={{ bg: "purple.700" }}
            _disabled={{ bg: "gray.300", cursor: "not-allowed" }}
          />
        </Box>
      </Tooltip>
    ),
    [isLoading, isBlockedByAnalystLoading, handleSend, isLoadingChat, isInputDisabledDueToEva]
  );

  // Render
  return (
    <>
      {mentionQuery !== null && (
        <ArtifactMentionMenu
          ref={mentionMenuRef}
          sections={mentionSections}
          onSelect={handleMentionSelect}
          onClose={() => setMentionQuery(null)}
          anchorRef={inputWrapperRef}
        />
      )}
      <Box
        ref={inputWrapperRef}
        position="relative"
        w={width}
        border="1px solid"
        borderColor={isDragOver ? "purple.400" : "gray.300"}
        borderRadius="xl"
        bg="white"
        overflow="hidden"
        _focusWithin={{ borderColor: "purple.500", boxShadow: "0 0 0 1px var(--chakra-colors-purple-600)" }}
        boxShadow={isDragOver ? "0 0 0 2px var(--chakra-colors-purple-300)" : "sm"}
        transition="border-color 0.15s, box-shadow 0.15s"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isDragOver && (
          <Box
            position="absolute"
            inset={0}
            zIndex={10}
            bg="purple.50"
            borderRadius="xl"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap={1}
            pointerEvents="none"
          >
            <Icon as={AppIcons.upload} boxSize={6} color="purple.400" />
            <Text fontSize="sm" fontWeight="600" color="purple.600" m={0}>
              Drop files here
            </Text>
          </Box>
        )}
        <MessageInput
          id={!hasMessages ? "chat-input" : undefined}
          ref={highlightedTextareaRef}
          value={inputMessage}
          onChange={handleInputChange}
          onKeyDown={handleKeyPress}
          onPasteFiles={handleNonCsvFileSelected}
          placeholder={hasMessages ? "Reply..." : "How can I help?"}
          previewText={previewText}
          minHeight={hasMessages ? "44px" : "90px"}
          maxHeight="150px"
          isDisabled={isLoadingChat || isInputDisabledDueToEva}
          lookupByName={lookupByName}
          fontSize="md"
          px={3}
          pt={3}
          pb={1}
          fontWeight={hasMessages ? undefined : "500"}
        />
        {pendingFiles.length > 0 && (
          <Box px={3} pb={2} display="flex" flexWrap="wrap" gap={2} justifyContent="flex-start">
            {pendingFiles.map((entry, idx) => {
              const isImage = /\.(png|jpe?g)$/i.test(entry.fileName);
              const fileTypeLabel = /\.pdf$/i.test(entry.fileName)
                ? "PDF"
                : /\.docx$/i.test(entry.fileName)
                  ? "DOCX"
                  : isImage
                    ? "Image"
                    : "CSV";
              return (
                <HStack
                  key={`${entry.fileName}-${idx}`}
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="lg"
                  px={3}
                  py={2}
                  spacing={2}
                  maxW="280px"
                >
                  {isImage ? (
                    <Box
                      as="img"
                      src={URL.createObjectURL(entry.file)}
                      alt={entry.fileName}
                      boxSize="32px"
                      objectFit="cover"
                      borderRadius="md"
                      flexShrink={0}
                    />
                  ) : (
                    <Icon as={AppIcons.upload} boxSize={4} color="purple.600" flexShrink={0} />
                  )}
                  <VStack spacing={0} align="flex-start" flex={1} minW={0}>
                    <Text fontSize="xs" fontWeight="600" color="gray.800" m={0} noOfLines={1}>
                      {entry.fileName.replace(/\.(csv|pdf|docx|png|jpe?g)$/i, "")}
                    </Text>
                    <Text fontSize="xs" color="gray.500" m={0}>
                      {(entry.file.size / 1024).toFixed(1)} KB · {fileTypeLabel}
                    </Text>
                  </VStack>
                  <IconButton
                    icon={<Icon as={AppIcons.close} boxSize={2} />}
                    size="xs"
                    variant="ghost"
                    colorScheme="gray"
                    aria-label="Remove file"
                    onClick={() => setPendingFiles(prev => prev.filter((_, i) => i !== idx))}
                    flexShrink={0}
                  />
                </HStack>
              );
            })}
          </Box>
        )}
        <HStack justify="space-between" spacing={2} m={2}>
          {/* Left: + menu */}
          <Menu>
            <MenuButton
              as={IconButton}
              icon={<Icon as={AppIcons.plusLine} boxSize={5} color="purple.600" />}
              size="sm"
              variant="ghost"
              borderRadius="full"
              bg="purple.50"
              _hover={{ bg: "purple.100" }}
              aria-label="Add attachment"
              flexShrink={0}
            />
            <MessageInputAddMenu
              sessionHeader={currentSessionHeader}
              analyst={selectedAnalyst ?? undefined}
              chatId={chatId ?? ""}
              scenarioJobs={scenarioJobs}
              onSelectAnalysis={handleAnalysisSelect}
              onCsvFileSelected={handleCsvFileSelected}
              onNonCsvFileSelected={handleNonCsvFileSelected}
            />
          </Menu>
          {/* Right: analyst selector + send */}
          <HStack spacing={2}>
            {renderAnalystSelector(4, "xs")}
            {hasMessages ? renderSendButton("sm", 4, 0) : renderSendButton("md", 5)}
          </HStack>
        </HStack>
      </Box>
    </>
  );
};

export default MessageInputBox;
